import type { Source } from '@eformulieren/model'
import type * as Y from 'yjs'
import { sourcesOf } from '../document/sectionDocument.js'
import { type CitationField, collectClusters } from './collectClusters.js'
import { createCitationEngine } from './engine.js'
import { citationPlainText } from './plainText.js'
import type { CitationCluster, CitationResult } from './types.js'

/** De citatie-uitkomst van één sectie, met per citatie het label zoals de tekst het toont. */
export interface SectionCitations extends CitationResult {
  /**
   * Per `citatieId` de vermelding als platte tekst: wat de `referentie`-knoop in de tekst toont, en
   * precies wat de revisie als vermelding vastlegt (`exportSectionSources`).
   */
  readonly labels: ReadonlyMap<string, string>
  /**
   * Per `citatieId` de regels uit de literatuurlijst van de bronnen die ze citeert, in de volgorde
   * van de citatie en zonder dubbele: de volledige bronvermelding in de popover (naar CitatenPOC).
   */
  readonly entries: ReadonlyMap<string, readonly string[]>
}

/**
 * De citatie-uitkomst van één sectiedocument, in één keer en zonder React (R-50).
 *
 * Dezelfde berekening als `useSectionCitations`, voor een stand die niet meer meeloopt: een oudere
 * stand in de geschiedenismodus (6.9), opgebouwd uit haar eigen revisie of snapshot. Die rekent haar
 * labels uit haar eigen bronnen en citaties, en niet uit die van nu: een bron die er later bijkwam
 * kan een label van "2026" naar "2026a" laten gaan, en dat hoort een oudere stand niet te tonen.
 */
export function sectionCitationsOf(doc: Y.Doc, fieldIds: readonly CitationField[]): SectionCitations {
  return citationsOf([{ sectieId: '', doc, fieldIds }])
}

/** Eén sectie die meetelt: haar document en haar rich-text-velden, in formuliervolgorde. */
export interface ScopeSection {
  readonly sectieId: string
  readonly doc: Y.Doc
  readonly fieldIds: readonly CitationField[]
}

/** De uitkomst over de secties van een laag, met per sectie wat zij zelf citeert. */
export interface ScopeCitations extends SectionCitations {
  /** De bronnen uit alle sectieopslagen, per id; bij twee dezelfde telt de eerste in formuliervolgorde. */
  readonly sources: ReadonlyMap<string, Source>
  readonly perSection: ReadonlyMap<string, { readonly citations: readonly string[]; readonly sources: ReadonlySet<string> }>
}

/**
 * De citaties over meerdere sectiedocumenten, in één nummering (R-50, eisenboek 12.9).
 *
 * Naar CitatenPOC, dat over het hele formulier rekent: R-50 vult de referentielijst "uit de
 * verwijzingen in de tekst van het voorstel". De documenten blijven per sectie (B6); dit leest ze
 * naast elkaar en voegt niets samen. Welke secties meetellen is aan de aanroeper: op het scherm de
 * secties van één laag in de projectie van de lezer, in de pod alle secties van die laag. Volgorde:
 * de secties zoals gegeven, daarbinnen de velden zoals gegeven.
 */
export function citationsOf(sections: readonly ScopeSection[]): ScopeCitations {
  return createScopeCitations()(sections)
}

/**
 * Dezelfde berekening met één engine die blijft staan, voor wie vaak opnieuw rekent (de hook): de
 * engine opnieuw bouwen kost meer dan een herberekening.
 */
export function createScopeCitations(): (sections: readonly ScopeSection[]) => ScopeCitations {
  let list: readonly Source[] = []
  // Pas gebouwd bij de eerste citatie: een citeproc-engine bouwen kost tientallen milliseconden, en
  // een beurt zonder enige bronvermelding hoort dat niet te betalen (gemeten met de belastingtest
  // van indienen, `apps/runtime/keten/load-submit.test.ts`).
  let engine: ReturnType<typeof createCitationEngine> | undefined
  return (sections) => {
    const sources = new Map<string, Source>()
    const clusters: CitationCluster[] = []
    const perSection = new Map<string, { citations: string[]; sources: Set<string> }>()
    for (const section of sections) {
      for (const source of sourcesOf(section.doc)) if (!sources.has(source.id)) sources.set(source.id, source)
      const own = collectClusters(section.doc, section.fieldIds)
      clusters.push(...own)
      perSection.set(section.sectieId, {
        citations: own.map((cluster) => cluster.citatieId),
        sources: new Set(own.flatMap((cluster) => cluster.bronnen)),
      })
    }
    list = [...sources.values()]
    if (clusters.length === 0) return { ...withLabels({ inTekst: new Map(), literatuurlijst: [] }, clusters), sources, perSection }
    engine ??= createCitationEngine(() => list)
    return { ...withLabels(engine.process(clusters), clusters), sources, perSection }
  }
}

function withLabels(result: CitationResult, clusters: readonly CitationCluster[]): SectionCitations {
  const labels = new Map<string, string>()
  for (const [citationId, html] of result.inTekst) labels.set(citationId, citationPlainText(html))
  const byId = new Map(result.literatuurlijst.map((entry) => [entry.bronId as string, entry.html]))
  const entries = new Map<string, readonly string[]>()
  for (const cluster of clusters) {
    if (entries.has(cluster.citatieId)) continue
    const lines = [...new Set(cluster.bronnen)].flatMap((id) => {
      const html = byId.get(id)
      return html === undefined ? [] : [html]
    })
    if (lines.length > 0) entries.set(cluster.citatieId, lines)
  }
  return { ...result, labels, entries }
}
